import type { AboutUsTabsForm } from "./about-us-tabs-form";

export class AboutUsTabsHandler {
    private readonly form: AboutUsTabsForm;

    public constructor(form: AboutUsTabsForm) {
        this.form = form;
    }

    public bootstrap(): void {
        const initialTab = this.findTabByHash() ?? this.form.getTabs()[0];
        if (initialTab) this.activateTab(initialTab, { syncHash: false, scroll: false });
    }

    public handleTabClick(tab: HTMLElement): void {
        this.activateTab(tab, { syncHash: true, scroll: true });
    }

    public handlePopState(): void {
        const tab = this.findTabByHash();
        if (tab) this.activateTab(tab, { syncHash: false, scroll: false });
    }

    private activateTab(
        activeTab: HTMLElement,
        { syncHash, scroll }: { syncHash: boolean; scroll: boolean },
    ): void {
        const panelId = activeTab.getAttribute("aria-controls") ?? "";

        this.form.getTabs().forEach((tab) => {
            const isActive = tab === activeTab;
            tab.classList.toggle("is-active", isActive);
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
            tab.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        this.form.getPanels().forEach((panel) => {
            panel.hidden = panel.id !== panelId;
        });

        if (scroll) this.scrollToTabsBar();

        if (syncHash) {
            const nextHash = `#${panelId}`;
            if (window.location.hash !== nextHash) {
                history.pushState(null, "", nextHash);
            }
        }
    }

    private scrollToTabsBar(): void {
        const tabsBar = this.form.getTabsBar();
        const navbarHeight = this.form.getNavbar()?.offsetHeight ?? 0;
        const targetY = tabsBar.getBoundingClientRect().top + window.scrollY - navbarHeight;
        const startY = window.scrollY;
        const distance = targetY - startY;
        if (Math.abs(distance) < 1) return;
        const duration = 700;
        const startTime = performance.now();
        const easeInOutCubic = (t: number): number =>
            t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const tick = (now: number): void => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            window.scrollTo(0, startY + distance * easeInOutCubic(progress));
            if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }

    private findTabByHash(): HTMLElement | null {
        const hash = typeof window !== "undefined" ? window.location.hash.slice(1).trim() : "";
        if (!hash) return null;
        return this.form.getTabs().find((tab) => tab.getAttribute("aria-controls") === hash) ?? null;
    }
}
